import { useState } from 'react'
import Box from '@mui/joy/Box'
import Checkbox from '@mui/joy/Checkbox'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'

import { useUpdateReservationSendSms } from '@/hooks/api/use-reservation-checkout'

const CHECKOUT_SEND_SMS_CHECKBOX_ID = 'checkout-send-sms-preference'
const CHECKOUT_SEND_SMS_TEXT_ID = 'checkout-send-sms-preference-text'
const CHECKOUT_SEND_SMS_ERROR_ID = 'checkout-send-sms-preference-error'

export default function CheckoutSendSmsPreference({
    sendSms,
    alreadyKnowGarage,
    isPwa = false,
}: {
    sendSms: boolean
    alreadyKnowGarage: boolean
    isPwa?: boolean
}) {
    if (!alreadyKnowGarage || isPwa) {
        return null
    }

    return <CheckoutSendSmsPreferenceCheckbox sendSms={sendSms} />
}

function CheckoutSendSmsPreferenceCheckbox({
    sendSms,
}: {
    sendSms: boolean
}) {
    const { t } = useTranslation()
    const [requestInProgress, setRequestInProgress] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const updateReservationSendSms = useUpdateReservationSendSms({
        setRequestInProgress,
        onSuccessCallback: () => {
            setErrorMessage(null)
        },
        onErrorCallback: setErrorMessage,
    })

    const checked = !sendSms

    return (
        <Box sx={{ minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pl: 1,
                }}
            >
                <Checkbox
                    id={CHECKOUT_SEND_SMS_CHECKBOX_ID}
                    checked={checked}
                    disabled={requestInProgress}
                    onChange={event => {
                        setErrorMessage(null)
                        setRequestInProgress(true)
                        updateReservationSendSms.mutate({
                            send_sms: !event.target.checked,
                        })
                    }}
                    color={errorMessage ? 'danger' : 'neutral'}
                    size="lg"
                    slotProps={{
                        input: {
                            'aria-labelledby': CHECKOUT_SEND_SMS_TEXT_ID,
                            'aria-describedby': errorMessage ? CHECKOUT_SEND_SMS_ERROR_ID : undefined,
                            'aria-invalid': Boolean(errorMessage),
                        },
                    }}
                    sx={{
                        m: 0,
                        '& .MuiCheckbox-checkbox': {
                            mt: '2px',
                        },
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0, pt: '1px' }}>
                    <Typography
                        id={CHECKOUT_SEND_SMS_TEXT_ID}
                        component="label"
                        htmlFor={CHECKOUT_SEND_SMS_CHECKBOX_ID}
                        level="body-xs"
                        sx={{
                            display: 'block',
                            cursor: requestInProgress ? 'default' : 'pointer',
                            textAlign: 'left',
                        }}
                    >
                        {t('checkout.sendSms')}
                    </Typography>
                </Box>
            </Box>
            {errorMessage ? (
                <Typography
                    id={CHECKOUT_SEND_SMS_ERROR_ID}
                    level="body-xs"
                    color="danger"
                    sx={{ mt: 0.5, pl: '44px' }}
                >
                    {errorMessage}
                </Typography>
            ) : null}
        </Box>
    )
}
